import {
  calculateTaxFailure,
  calculateTaxStarted,
  calculateTaxSucceeded,
  taxPaymentPeriodFailure,
  taxPaymentPeriodsStarted,
  taxPaymentPeriodSucceeded,
} from './taxSlice';
import api from '../../../api';
import { AppThunk } from 'src/store/store';
import { deriveConfigExpressTaxCertifications } from '../config/configSelectors';
import { setPaymentID, setPaymentFees } from '../payment/paymentSlice';
import { TaxConfigurationInformation } from 'src/types/configuration';
import { CalculatedTot, PeriodAndLicense, PeriodLicense } from 'src/types/tax';
import { PaymentFees, PaymentPeriod } from 'src/types/payment';
import { C_PERMIT_STATUS_CURRENT } from 'src/common/consts';
import { roundDecimalPlaces } from 'src/common/utils';

export const getTaxPaymentPeriodsAsync = (
  completionCallback = () => null
): AppThunk<void, typeof api> => {
  return async (dispatch, getState, api) => {
    dispatch(taxPaymentPeriodsStarted());
    try {
      const paymentPeriods = await api.getTaxPaymentPeriods(getState());
      if (paymentPeriods.status === 200) {
        const licenses = await api.getAllLicensesForUserAsync(
          getState().auth?.idToken
        );

        // Determine if a license is considered to be inactive
        // A license in inactive if its status is not "current" and it is not actionable
        (paymentPeriods?.data?.paymentPeriods as Array<PaymentPeriod>).forEach(
          (period) => {
            period.licenses.forEach((paymentLicense) => {
              let isInactive = false;
              if (licenses) {
                const matchedLicense = licenses.find(
                  (license) =>
                    paymentLicense !== null &&
                    license.license === paymentLicense?.license
                );

                if (
                  matchedLicense?.status !== C_PERMIT_STATUS_CURRENT &&
                  !paymentLicense?.isActionable
                ) {
                  isInactive = true;
                }
              }
              paymentLicense.isInactive = isInactive;
            });
          }
        );

        dispatch(
          taxPaymentPeriodSucceeded({
            ...paymentPeriods.data,
            callback: completionCallback,
          })
        );
      } else {
        dispatch(
          taxPaymentPeriodFailure(
            'Something went wrong while fetching tax payment periods.'
          )
        );
      }
    } catch {
      dispatch(
        taxPaymentPeriodFailure(
          'Something went wrong while fetching payment periods.'
        )
      );
    }
  };
};

const getTaxableActivitiesFromBuckets = (
  license: PeriodLicense,
  dateStart: string,
  dateEnd: string
) => {
  return license.buckets?.map((bucket) => {
    const totalRevenue = bucket.revenue ?? 0;
    const allowableDeductions = bucket.discounts ? Number(bucket.discounts) : 0;
    const taxableReceipts = roundDecimalPlaces(
      Number(totalRevenue) - Number(allowableDeductions)
    );
    return {
      dateStart,
      dateEnd,
      taxableReceipts,
      totalRevenue,
      allowableDeductions,
      numDaysOccupied: license.occupancy?.occupied || 0,
      numDaysAvailable: license.occupancy?.available || 0,
      provider: bucket.provider,
    };
  });
};

const transformTaxableReceiptsForCalculateMultiPropertyTot = (
  paymentPeriods: Array<PeriodAndLicense>
) => {
  return paymentPeriods.flatMap((paymentPeriod) => {
    return paymentPeriod.licenses.map((license) => {
      const taxableActivities = getTaxableActivitiesFromBuckets(
        license,
        paymentPeriod.startDate,
        paymentPeriod.endDate
      );

      return {
        license: license.license,
        dateStart: paymentPeriod.startDate,
        dateEnd: paymentPeriod.endDate,
        misc: {
          taxableActivities,
        },
      };
    });
  });
};

const calculateBreakdownValue = (data: CalculatedTot, key: string) => {
  let totalDue = 0;
  data.details.forEach((detail) => {
    if (detail.misc && detail.misc.taxableActivities) {
      detail.misc.taxableActivities.forEach((activity) => {
        if (activity.taxTypes) {
          Object.values(activity.taxTypes).forEach((taxType) => {
            totalDue += taxType[key] || 0;
          });
        }
      });
    }
  });
  return totalDue;
};

const calculateBreakdownValueForTaxType = (
  data: CalculatedTot,
  taxTypeKey: string,
  key: string
) => {
  let totalDue = 0;
  data.details.forEach((detail) => {
    if (detail.misc && detail.misc.taxableActivities) {
      detail.misc.taxableActivities.forEach((activity) => {
        if (activity.taxTypes && activity.taxTypes[taxTypeKey]) {
          totalDue += activity.taxTypes[taxTypeKey][key] || 0;
        }
      });
    }
  });
  return totalDue;
};

const configTaxTypeKeyExistsInData = (
  data: CalculatedTot,
  configKey: string
): boolean => {
  return data.details.some((detail) =>
    detail.misc.taxableActivities.some(
      (activity) => activity.taxTypes[configKey]
    )
  );
};

const transformDataForPaymentFees = (
  data: CalculatedTot,
  taxConfig: Array<TaxConfigurationInformation>
) => {
  // save payment data in redux
  // get total for all breakdown items in the tax config
  const breakdown: Array<{ key: string; label: string; value: number }> = [];

  // each tax type is treated individually
  taxConfig.forEach((config) => {
    // only include tax type if it is in the config and there is a matching item in the data
    if (configTaxTypeKeyExistsInData(data, config.key)) {
      config.breakdown.forEach((item) => {
        if (item.key === 'totDue') {
          const value = calculateBreakdownValueForTaxType(
            data,
            config.key,
            item.key
          );
          breakdown.push({
            key: `${config.key}_${item.key}`,
            label: config.label,
            value,
          });
        }
      });
    }
  });

  // fees are summed across tax types
  taxConfig.forEach((config) => {
    // only include tax type if it is in the config and there is a matching item in the data
    if (configTaxTypeKeyExistsInData(data, config.key)) {
      config.breakdown.forEach((item) => {
        if (
          !breakdown.some((breakdownItem) => breakdownItem.key === item.key)
        ) {
          if (item.key !== 'totDue') {
            const sum = calculateBreakdownValue(data, item.key);
            breakdown.push({ key: item.key, label: item.label, value: sum });
          }
        }
      });
    }
  });

  const paymentFees: PaymentFees = {
    ccFee: data?.ccFee,
    ccTotal: data?.ccTotal,
    achFee: data?.achFee,
    achTotal: data?.achTotal,
    checkFee: data?.checkFee,
    checkTotal: data?.checkTotal,
    breakdown,
  };

  return paymentFees;
};

const prepareMandatoryAcknowledgements = (taxCertifications: string[] = []) =>
  taxCertifications.reduce(
    (acknowledgements, certification, index) => ({
      ...acknowledgements,
      [`aff_${index + 1}`]: { label: certification },
    }),
    {}
  );

export const calculateMultiPropertyTaxAsync = (
  paymentPeriods: Array<PeriodAndLicense>,
  taxConfig: Array<TaxConfigurationInformation>
): AppThunk<Promise<boolean>, typeof api> => {
  return async (dispatch, getState, api) => {
    dispatch(calculateTaxStarted());

    const CHUNK_SIZE = 25;

    const convertedPaymentPeriods =
      transformTaxableReceiptsForCalculateMultiPropertyTot(paymentPeriods);

    const mandatoryAcknowldgements =
      deriveConfigExpressTaxCertifications(getState());
    const preparedMandatoryAcknowldgement = prepareMandatoryAcknowledgements(
      mandatoryAcknowldgements
    );

    try {
      let data: CalculatedTot = { details: [] };
      let paymentId = null;

      for (
        let chunkStartIdx = 0;
        chunkStartIdx < convertedPaymentPeriods.length;
        chunkStartIdx += CHUNK_SIZE
      ) {
        const chunkEndIdx = Math.min(
          convertedPaymentPeriods.length,
          chunkStartIdx + CHUNK_SIZE
        );

        const paymentPeriodsInChunk = convertedPaymentPeriods.slice(
          chunkStartIdx,
          chunkEndIdx
        );

        const requestBody = {
          paymentPeriods: paymentPeriodsInChunk,
          numPaymentPeriods: convertedPaymentPeriods.length,
          paymentId,
          mandatoryAcknowldgements: preparedMandatoryAcknowldgement,
        };

        const result = await api.calculateMultiPropertyTotAsync(
          getState(),
          requestBody
        );
        data = result.data;
        paymentId = data.paymentId;
      }
      if (data) {
        // save payment data in redux
        const paymentFees = transformDataForPaymentFees(data, taxConfig);

        // save raw response in redux
        dispatch(calculateTaxSucceeded(data));

        // update paymentID in redux
        dispatch(setPaymentID(data.paymentId));

        dispatch(setPaymentFees(paymentFees));
      }
      return true;
    } catch (error) {
      dispatch(
        calculateTaxFailure('Something went wrong while calculating tax.')
      );
      return false;
    }
  };
};
